import { h } from 'preact';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { useDispatch, useSelector } from 'react-redux';

import { Map as MapIcon } from '@travel/icons/ui';
import { Translate } from '@travel/translation';
import { useDeviceTypes } from '@travel/traveler-core/hooks';
import { getSearchRatParams } from '@travel/traveler-core/utils/jrdpHelper';
import isNotEmptyArray from '@travel/ui/utils/arrays';
import cx from '@travel/ui/utils/classnames';
import { stringify } from '@travel/utils';

import Breadcrumb from 'components/BreadCrumb';
import DataLayer from 'components/DataLayer';
import GoToTopButton from 'components/GoToTopButton';
import HotelMap from 'components/HotelMap';
import InternalLinksCard from 'components/InternalLinksCard';
import ItineraryLink from 'components/ItineraryLink';
import ProviderListSearchHeader from 'components/ProviderListSearchHeader';
import Loading from 'pages/HotelList/components/Loading';
import { setIsFirstTimeLoading } from 'store/hotelList/actions';
import {
  getHotelList,
  getHotelListBreadcrumb,
  getIsFetching,
  getIsFirstTimeLoading,
} from 'store/hotelList/selectors';
import { filterInternalLinks } from 'utils/internalLinks';
import List from './components/List';

import { pushLocation } from 'store/__router/actions';

import paths from 'constants/paths';
import { getDataLayersArrayParams, getPlaceLayers, getValidAccommodationsPath } from 'utils/search';
import { GSP_JRDP_MAPPING } from '@travel/traveler-core/constants/JRDPAnalytics';
import { getSeo } from 'store/seo/selectors';

import styles from './hotelList.module.scss';

import type { URLSearchQuery } from 'Common-Types';
import type { URLParams } from './resolver';

export type Props = {
  /** Object of the page identifiers */
  params: URLParams;
  /** Object of current active search */
  query?: URLSearchQuery;
};

const HotelList = (props: Props) => {
  const { params, query } = props;

  const breadcrumbs = useSelector(getHotelListBreadcrumb);
  const { hotels } = useSelector(getHotelList);
  const { data } = useSelector(getSeo);
  const isFirstTimeLoading = useSelector(getIsFirstTimeLoading);
  const isFetching = useSelector(getIsFetching);
  const dispatch = useDispatch();
  const { isPC } = useDeviceTypes();
  const mapRef = useRef<google.maps.Map | null>(null);

  const pageIdentifier = params.pageIdentifier ?? '';
  const pageNumber = params.pageNumber;
  const accommodationAreaPath = hotels.accommodationArea?.pathId;
  const accommodationsPath = getValidAccommodationsPath(hotels.accommodations);
  const accommodationArea = hotels.accommodationArea?.name;
  const accommodationsOrSearchKey = accommodationsPath ? accommodationsPath : pageIdentifier;

  const isProviderValid = isNotEmptyArray(hotels.providers);
  const dataLayersArrayParams = isProviderValid ? getDataLayersArrayParams(hotels.providers) : {};
  const setMapInstance = useCallback(mapObj => (mapRef.current = mapObj), []);
  const mapUrl = paths.providerList.pathResolver(params.pageIdentifier);

  const internalLinks = filterInternalLinks(hotels?.internalLinks);
  const placeLayers = getPlaceLayers(breadcrumbs, query);

  const switchView = useCallback(() => {
    dispatch(
      pushLocation({
        pathname: paths.providerListMap.pathResolver(params.pageIdentifier),
        search: stringify({ ...props.query }),
      }),
    );
  }, [dispatch, props.query, params.pageIdentifier]);

  const [isShowingLoadingAnimation, setIsShowingLoadingAnimation] = useState(isFirstTimeLoading);
  const [hasObligatoryTimePassed, setHasObligatoryTimePassed] = useState(false);
  const onMapClick = e => {
    switchView();
    e.preventDefault();
  };

  useEffect(() => {
    if (isFirstTimeLoading) {
      setTimeout(() => setHasObligatoryTimePassed(true), 1500);
    }
     
  }, []);

  useEffect(() => {
    if (hasObligatoryTimePassed) {
      setIsShowingLoadingAnimation(isFetching);
      if (!isFetching) {
        dispatch(setIsFirstTimeLoading(false));
      }
    }
  }, [dispatch, hasObligatoryTimePassed, isFetching]);

  return (
    <div className={cx(isShowingLoadingAnimation && styles.loadingAnimationContainer)}>
      {isShowingLoadingAnimation && <Loading />}
      <div className={styles.hotelListContainer}>
        {isPC && <Breadcrumb placeLayers={placeLayers} className={styles.breadcrumb} />}
        <ItineraryLink
          title={<Translate id="Hotel_List.Change_Accommodations.Title" />}
          query={query}
        />
        <ProviderListSearchHeader
          query={props.query}
          pathName={paths.providerList.pathResolver(pageIdentifier)}
          totalCount={hotels.total}
        />
        {!isPC ? (
          <>
            <List
              hotels={hotels}
              isSP={true}
              isFetching={isFetching}
              isShowingLoadingAnimation={isShowingLoadingAnimation}
              urlSearchQuery={props.query}
              pageIdentifier={pageIdentifier}
              pageNumber={pageNumber}
              accommodationAreaPath={accommodationAreaPath}
              accommodationsPath={accommodationsPath}
            />
          </>
        ) : (
          <div className={styles.splitStyle}>
            <List
              hotels={hotels}
              isSP={false}
              isFetching={isFetching}
              isShowingLoadingAnimation={isShowingLoadingAnimation}
              urlSearchQuery={props.query}
              pageIdentifier={pageIdentifier}
              pageNumber={pageNumber}
              accommodationAreaPath={accommodationAreaPath}
              accommodationsPath={accommodationsPath}
            />

            {isFetching || isProviderValid ? (
              <div data-testid="hotelList-mapContainer" className={styles.mapWrapper}>
                {isProviderValid ? (
                  <HotelMap
                    setMapInstance={setMapInstance}
                    switchView={switchView}
                    hotels={hotels}
                    searchQuery={props.query}
                    isLimitedView={true}
                    isFetching={false}
                    pathId={params.pageIdentifier}
                    accommodations={hotels.accommodations}
                  />
                ) : (
                  <div class={styles.mapSkeleton} />
                )}
              </div>
            ) : null}
          </div>
        )}
        {!isPC && <ItineraryLink query={query} />}
        {!isPC && <Breadcrumb placeLayers={placeLayers} className={styles.breadcrumb} />}
        {!isPC && isProviderValid && isNotEmptyArray(hotels.providers) && (
          <GoToTopButton className={styles.goToTopButton} />
        )}
        {isNotEmptyArray(internalLinks) && !data?.isDatedTypeThreeUrl && (
          <InternalLinksCard heading={accommodationArea} data={internalLinks} />
        )}
        {!isPC && isNotEmptyArray(hotels.providers) && (
          <>
            <div
              className={cx(styles.mapButtonWrapper, isShowingLoadingAnimation && styles.loading)}
            >
              <a
                href={mapUrl}
                onClick={onMapClick}
                className={styles.mapButton}
                data-testid="searchMenuBox-spMapOpen-button"
              >
                <span className={styles.iconMap}>
                  <MapIcon size={16} color="cilantro" />
                </span>
                <Translate id="Hotel_List.Display_Option.Map_View" />
              </a>
            </div>
            <div className={styles.mapButtonContainer} />
          </>
        )}
      </div>
      {hotels.accommodationArea && (
        <DataLayer
          page="hotel_list"
          {...getSearchRatParams(query)}
          {...dataLayersArrayParams}
          searchKey={accommodationsOrSearchKey}
          destinationArea={accommodationsOrSearchKey}
          isBookable={isProviderValid ? 'bookable' : 'unbookable'}
          stepParam="step1"
          cartModifyHotelList={query?.transactionId ? 1 : ''}
          gspRequestId={hotels.gspRequestId}
          providerListGsp={GSP_JRDP_MAPPING[hotels.searchEngine]}
        />
      )}
    </div>
  );
};

export default HotelList;
